import { randomDelay } from "@/lib/utils/sleep";

/** Regime da distribuição: um responsável, carteira aberta ou por equipe. */
export type AssignmentMode = "exclusive" | "pool" | "team";

export interface AgencyAssignment {
  id: string;
  propertyId: string;
  brokerId: string;
  brokerName: string;
  linkType: "internal" | "external";
  mode: AssignmentMode;
  commissionPercentage: number | null;
  status: "pending" | "active" | "declined" | "revoked";
  assignedAt: string;
  respondedAt: string | null;
}

/**
 * Pulso do imóvel (hub#690). `daysWithoutActivity` é NULO quando nunca houve atividade —
 * zero diria que houve movimento hoje, que é a leitura oposta.
 */
export interface PropertyActivity {
  visits7d: number;
  visits30d: number;
  leads: number;
  openProposals: number;
  rejectedProposals: number;
  openDeals: number;
  hasActiveContract: boolean;
  lastActivityAt: string | null;
  daysWithoutActivity: number | null;
}

export interface CycleProposal {
  id: string;
  internalCode: string;
  status: string;
  clientName: string;
  amount: number | null;
  brokerName: string;
  rejectionReason: string | null;
  createdAt: string;
  respondedAt: string | null;
}

export interface CycleDeal {
  id: string;
  internalCode: string;
  status: string;
  clientName: string;
  amount: number;
  commissionTotal: number;
  brokerName: string;
  createdAt: string;
  closedAt: string | null;
}

export interface CycleContract {
  id: string;
  internalCode: string;
  status: string;
  type: string;
  value: number | null;
  startDate: string | null;
  endDate: string | null;
  signedAt: string | null;
}

export interface CycleInspection {
  id: string;
  status: string;
  reportStatus: string;
  scheduledFor: string | null;
  completedAt: string | null;
}

/** Ciclo completo do imóvel da casa — com PII do cliente (a imobiliária é a intermediadora). */
export interface PropertyCycle {
  propertyId: string;
  title: string;
  internalCode: string;
  publicCode: string | null;
  status: string;
  activity: PropertyActivity;
  proposals: CycleProposal[];
  deals: CycleDeal[];
  contracts: CycleContract[];
  inspections: CycleInspection[];
}

export interface AgencyProperty {
  id: string;
  title: string;
  publicCode: string | null;
  internalCode: string;
  status: string;
  operation: string;
  salePrice: number | null;
  rentPrice: number | null;
  city: string | null;
  neighborhood: string | null;
  createdAt: string;
  assignments: AgencyAssignment[];
  activity?: PropertyActivity | null;
}

export interface CreateAgencyPropertyInput {
  title: string;
  description?: string | null;
  type: string;
  category: string;
  operation: string;
  salePrice?: number | null;
  rentPrice?: number | null;
  condoFee?: number | null;
  iptuYearly?: number | null;
  address: {
    cep?: string | null;
    street: string;
    number: string;
    complement?: string | null;
    neighborhood: string;
    city: string;
    uf: string;
  };
  bedrooms?: number | null;
  suites?: number | null;
  bathrooms?: number | null;
  parkingSpots?: number | null;
  builtArea?: number | null;
  totalArea?: number | null;
}

export interface DistributeInput {
  brokerIds: string[];
  mode: AssignmentMode;
  commissionPercentage?: number | null;
}

let seq = 1;
const properties: AgencyProperty[] = [
  {
    id: "p1",
    title: "Apartamento 3 quartos — Boa Viagem",
    publicCode: "IMV-000101",
    internalCode: "IMV-00001",
    status: "active",
    operation: "sale",
    salePrice: 850000,
    rentPrice: null,
    city: "Recife",
    neighborhood: "Boa Viagem",
    createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    activity: {
      visits7d: 8,
      visits30d: 34,
      leads: 3,
      openProposals: 1,
      rejectedProposals: 1,
      openDeals: 1,
      hasActiveContract: false,
      lastActivityAt: new Date(Date.now() - 86400000).toISOString(),
      daysWithoutActivity: 1,
    },
    assignments: [
      {
        id: "a1",
        propertyId: "p1",
        brokerId: "b1",
        brokerName: "Carlos Ferreira",
        linkType: "internal",
        mode: "exclusive",
        commissionPercentage: null,
        status: "active",
        assignedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
        respondedAt: null,
      },
    ],
  },
  {
    id: "p2",
    title: "Sala comercial — Centro",
    publicCode: null,
    internalCode: "IMV-00002",
    status: "active",
    operation: "rent",
    salePrice: null,
    rentPrice: 3500,
    city: "Recife",
    neighborhood: "Centro",
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    // Imóvel sem nenhum sinal de vida: dias parados é NULO, não zero.
    activity: {
      visits7d: 0,
      visits30d: 0,
      leads: 0,
      openProposals: 0,
      rejectedProposals: 0,
      openDeals: 0,
      hasActiveContract: false,
      lastActivityAt: null,
      daysWithoutActivity: null,
    },
    assignments: [],
  },
];

export async function listProperties(): Promise<AgencyProperty[]> {
  await randomDelay();
  return properties;
}

export async function createProperty(input: CreateAgencyPropertyInput): Promise<AgencyProperty> {
  await randomDelay(600, 1200);
  seq += 1;
  const created: AgencyProperty = {
    id: `p${seq}`,
    title: input.title,
    publicCode: null,
    internalCode: `IMV-${String(seq).padStart(5, "0")}`,
    status: "active",
    operation: input.operation,
    salePrice: input.salePrice ?? null,
    rentPrice: input.rentPrice ?? null,
    city: input.address.city,
    neighborhood: input.address.neighborhood,
    createdAt: new Date().toISOString(),
    assignments: [],
  };
  properties.unshift(created);
  return created;
}

export async function distributeProperty(
  propertyId: string,
  input: DistributeInput,
): Promise<AgencyAssignment[]> {
  await randomDelay(500, 900);
  const property = properties.find((p) => p.id === propertyId);
  if (!property) return [];

  const created = input.brokerIds.map((brokerId, index) => ({
    id: `a${Date.now()}-${index}`,
    propertyId,
    brokerId,
    brokerName: "Corretor",
    linkType: "internal" as const,
    mode: input.mode,
    commissionPercentage: input.commissionPercentage ?? null,
    status: "active" as const,
    assignedAt: new Date().toISOString(),
    respondedAt: null,
  }));

  property.assignments = [...property.assignments, ...created];
  return created;
}

export async function revokeAssignment(assignmentId: string): Promise<void> {
  await randomDelay(300, 600);
  for (const property of properties) {
    property.assignments = property.assignments.filter((a) => a.id !== assignmentId);
  }
}

export async function getPropertyCycle(propertyId: string): Promise<PropertyCycle> {
  await randomDelay();
  const property = properties.find((p) => p.id === propertyId);
  return {
    propertyId,
    title: property?.title ?? "Imóvel",
    internalCode: property?.internalCode ?? "IMV-00000",
    publicCode: property?.publicCode ?? null,
    status: property?.status ?? "active",
    activity: property?.activity ?? {
      visits7d: 0,
      visits30d: 0,
      leads: 0,
      openProposals: 0,
      rejectedProposals: 0,
      openDeals: 0,
      hasActiveContract: false,
      lastActivityAt: null,
      daysWithoutActivity: null,
    },
    proposals: [
      {
        id: "pr1",
        internalCode: "PRO-00012",
        status: "rejected",
        clientName: "Marina Alves",
        amount: 790000,
        brokerName: "Carlos Ferreira",
        rejectionReason: "Cliente achou o valor acima do mercado.",
        createdAt: new Date(Date.now() - 12 * 86400000).toISOString(),
        respondedAt: new Date(Date.now() - 9 * 86400000).toISOString(),
      },
    ],
    deals: [
      {
        id: "d1",
        internalCode: "VEN-00007",
        status: "inprogress",
        clientName: "Rafael Souza",
        amount: 845000,
        commissionTotal: 50700,
        brokerName: "Carlos Ferreira",
        createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
        closedAt: null,
      },
    ],
    contracts: [],
    inspections: [],
  };
}
